import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/client';
import { Spinner } from '../ds';
import emptyStateSvg from './empty-state.svg';
import './AdReport.css';

const fmt = (val) => val != null ? val.toLocaleString() : '—';
const pct = (val) => val != null && !isNaN(val) ? (val * 100).toFixed(1) + '%' : '—';
const pctRaw = (val) => val != null && !isNaN(val) ? val.toFixed(1) + '%' : '—';
const safeDivide = (a, b) => b > 0 ? a / b : null;

// --- SVG Line Chart ---
function LineChart({ rows, timeUnit }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 300 });
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ w: width, h: height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (!rows || rows.length === 0) return null;

  const COLORS = {
    requests: '#8B5CF6',
    impressions: '#3B82F6',
    clicks: '#10B981',
    views: '#F59E0B',
  };
  const LABELS = { requests: 'Requests', impressions: 'Impressions', clicks: 'Clicks', views: 'Views' };

  const W = size.w, H = size.h;
  const PAD = { top: 20, right: 20, bottom: 30, left: 60 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const series = Object.keys(COLORS);
  const allValues = series.flatMap((s) => rows.map((r) => r[s] || 0));
  const maxVal = Math.max(...allValues, 1);

  const xStep = rows.length > 1 ? plotW / (rows.length - 1) : plotW / 2;
  const getX = (i) => PAD.left + (rows.length > 1 ? i * xStep : plotW / 2);
  const getY = (val) => PAD.top + plotH - (val / maxVal) * plotH;

  const makeLine = (key) =>
    rows.map((r, i) => `${getX(i)},${getY(r[key] || 0)}`).join(' ');

  const formatLabel = (time) => {
    if (!time) return '';
    if (timeUnit === 'hour') return time.split(' ').pop() || time;
    return time.substring(5); // "MM-DD"
  };

  // Y axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxVal * f));

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    // x is already in pixel space matching SVG coords (1:1 viewBox)
    const svgX = x;
    // Find closest point
    let closestIdx = 0;
    let closestDist = Infinity;
    rows.forEach((_, i) => {
      const dist = Math.abs(svgX - getX(i));
      if (dist < closestDist) { closestDist = dist; closestIdx = i; }
    });
    if (closestDist < 40) {
      setTooltip({
        idx: closestIdx,
        x: (getX(closestIdx) / W) * 100,
        row: rows[closestIdx],
      });
    } else {
      setTooltip(null);
    }
  };

  return (
    <div className="ad-report__chart" onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
      {/* Legend */}
      <div className="ad-report__legend">
        {series.map((s) => (
          <span key={s} className="ad-report__legend-item">
            <span className="ad-report__legend-dot" style={{ background: COLORS[s] }} />
            {LABELS[s]}
          </span>
        ))}
      </div>

      <div ref={containerRef} className="ad-report__svg-wrap">
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="ad-report__svg">
          {/* Y axis ticks */}
          {yTicks.map((val, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={getY(val)} x2={W - PAD.right} y2={getY(val)}
                stroke="var(--border-color)" strokeWidth="0.5" />
              <text x={PAD.left - 8} y={getY(val) + 4} textAnchor="end"
                fontSize="10" fill="var(--text-tertiary)">{val.toLocaleString()}</text>
            </g>
          ))}

          {/* X axis labels */}
          {rows.map((r, i) => {
            // Show limited labels to avoid overlap
            const showLabel = rows.length <= 12 || i % Math.ceil(rows.length / 10) === 0;
            return showLabel ? (
              <text key={i} x={getX(i)} y={H - 5} textAnchor="middle"
                fontSize="10" fill="var(--text-tertiary)">{formatLabel(r.time)}</text>
            ) : null;
          })}

          {/* Lines */}
          {series.map((s) => (
            <polyline key={s} points={makeLine(s)} fill="none"
              stroke={COLORS[s]} strokeWidth="2" strokeLinejoin="round" />
          ))}

          {/* Data points */}
          {series.map((s) =>
            rows.map((r, i) => (
              <circle key={`${s}-${i}`} cx={getX(i)} cy={getY(r[s] || 0)} r="3"
                fill={COLORS[s]} />
            ))
          )}
        </svg>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div className="ad-report__tooltip" style={{ left: `${tooltip.x}%` }}>
          <div className="ad-report__tooltip-time">{tooltip.row.time}</div>
          {series.map((s) => (
            <div key={s} className="ad-report__tooltip-row">
              <span className="ad-report__legend-dot" style={{ background: COLORS[s] }} />
              {LABELS[s]}: {fmt(tooltip.row[s])}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Empty Chart (loading placeholder) ---
function EmptyChart() {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 300 });

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ w: width, h: height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const COLORS = {
    requests: '#8B5CF6',
    impressions: '#3B82F6',
    clicks: '#10B981',
    views: '#F59E0B',
  };
  const LABELS = { requests: 'Requests', impressions: 'Impressions', clicks: 'Clicks', views: 'Views' };
  const series = Object.keys(COLORS);

  const W = size.w, H = size.h;
  const PAD = { top: 20, right: 20, bottom: 30, left: 60 };
  const plotH = H - PAD.top - PAD.bottom;
  const getY = (f) => PAD.top + plotH - f * plotH;
  const yFractions = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="ad-report__chart" style={{ opacity: 0.5 }}>
      <div className="ad-report__legend">
        {series.map((s) => (
          <span key={s} className="ad-report__legend-item">
            <span className="ad-report__legend-dot" style={{ background: COLORS[s] }} />
            {LABELS[s]}
          </span>
        ))}
      </div>
      <div ref={containerRef} className="ad-report__svg-wrap">
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="ad-report__svg">
          {yFractions.map((f, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={getY(f)} x2={W - PAD.right} y2={getY(f)}
                stroke="var(--border-color)" strokeWidth="0.5" />
              <text x={PAD.left - 8} y={getY(f) + 4} textAnchor="end"
                fontSize="10" fill="var(--text-tertiary)">0</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

// --- Main Report Component ---
export default function AdReport({ adId, ad }) {
  const [timeUnit, setTimeUnit] = useState('date');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const toLocalDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const today = toLocalDate(new Date());

    if (ad && ad.start_time) {
      const adStart = ad.start_time.split('T')[0];
      const adEnd = ad.end_time ? ad.end_time.split('T')[0] : today;
      // 若廣告尚未結束（結束日 >= 今天）→ 用今天；已結束 → 用結束日
      const stillRunning = adEnd >= today;
      setStartTime(adStart);
      setEndTime(stillRunning ? today : adEnd);
    } else {
      // Fallback: last 7 days
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      setStartTime(toLocalDate(start));
      setEndTime(today);
    }
  }, [ad]);

  const fetchReport = useCallback(async () => {
    if (!startTime || !endTime) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getAdReport(adId, startTime, endTime, timeUnit);
      setReport(data);
    } catch (err) {
      setError(err.message || '無法載入報表');
    } finally {
      setLoading(false);
    }
  }, [adId, startTime, endTime, timeUnit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleTimeUnitChange = (val) => {
    setTimeUnit(val);
    if (val === 'hour') {
      const today = new Date().toISOString().split('T')[0];
      setStartTime(today);
      setEndTime(today);
    }
  };

  // Build totals row from rows data (recalculate rates)
  const buildTotalsRow = (rows) => {
    if (!rows || rows.length === 0) return null;
    const sum = { time: '合計' };
    const sumKeys = ['requests', 'served_impressions', 'downloaded_impressions', 'impressions',
      'clicks', 'views', 'engagements',
      'video_percentage_025', 'video_percentage_050', 'video_percentage_075', 'video_percentage_100'];
    sumKeys.forEach((k) => { sum[k] = 0; });
    rows.forEach((r) => { sumKeys.forEach((k) => { sum[k] += (r[k] || 0); }); });
    // Recalculate rates
    sum.fill_rate = safeDivide(sum.served_impressions, sum.requests);
    sum.viewability = safeDivide(sum.impressions, sum.served_impressions) != null
      ? safeDivide(sum.impressions, sum.served_impressions) * 100 : null;
    sum.click_rate = safeDivide(sum.clicks, sum.impressions);
    sum.view_rate = safeDivide(sum.views, sum.impressions);
    sum.engagement_rate = safeDivide(sum.engagements, sum.impressions);
    sum._isTotals = true;
    return sum;
  };

  const rows = report?.rows || [];
  const totalRequests = rows.reduce((s, r) => s + (r.requests || 0), 0);
  const isEmpty = rows.length === 0 || totalRequests === 0;
  const totalsRow = buildTotalsRow(rows);
  const tableData = totalsRow ? [...rows, totalsRow] : rows;

  const columns = [
    { key: 'time', title: timeUnit === 'hour' ? '時間' : '日期', width: '10%',
      render: (v, row) => row._isTotals ? <strong>{v}</strong> : v },
    { key: 'requests', title: '請求', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'served_impressions', title: 'Server 曝光', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'downloaded_impressions', title: '下載曝光', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'impressions', title: '可視曝光', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'fill_rate', title: '填充率', align: 'right', render: (v, row) => row._isTotals ? <strong>{pct(v)}</strong> : pct(v) },
    { key: 'viewability', title: '可視率', align: 'right', render: (v, row) => row._isTotals ? <strong>{pctRaw(v)}</strong> : pctRaw(v) },
    { key: 'clicks', title: '點擊', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'click_rate', title: 'CTR', align: 'right', render: (v, row) => row._isTotals ? <strong>{pct(v)}</strong> : pct(v) },
    { key: 'views', title: '觀看', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'view_rate', title: 'VTR', align: 'right', render: (v, row) => row._isTotals ? <strong>{pct(v)}</strong> : pct(v) },
    { key: 'video_percentage_025', title: '25%', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'video_percentage_050', title: '50%', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'video_percentage_075', title: '75%', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'video_percentage_100', title: '100%', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'engagements', title: '互動', align: 'right', render: (v, row) => row._isTotals ? <strong>{fmt(v)}</strong> : fmt(v) },
    { key: 'engagement_rate', title: 'ETR', align: 'right', render: (v, row) => row._isTotals ? <strong>{pct(v)}</strong> : pct(v) },
  ];

  return (
    <div className="ad-report">
      {/* Controls */}
      <div className="ad-report__controls">
        <select className="ad-report__select" value={timeUnit}
          onChange={(e) => handleTimeUnitChange(e.target.value)}>
          <option value="date">Daily</option>
          <option value="hour">Hourly</option>
        </select>
        <input type="date" className="ad-report__date" value={startTime}
          onChange={(e) => setStartTime(e.target.value)} />
        <span className="ad-report__separator">~</span>
        <input type="date" className="ad-report__date" value={endTime}
          onChange={(e) => setEndTime(e.target.value)} />
        <button className="ad-report__btn" onClick={fetchReport} disabled={loading}>查詢</button>
      </div>

      {error && <div className="ad-report__error">{error}</div>}

      {/* Chart area */}
      {loading ? (
        <div className="ad-report__chart-loading">
          <Spinner />
          <p className="ad-report__chart-loading-text">載入中…</p>
        </div>
      ) : !isEmpty ? (
        <LineChart rows={rows} timeUnit={timeUnit} />
      ) : report && isEmpty ? (
        <div className="ad-report__chart-empty">
          <img src={emptyStateSvg} alt="查無資料插圖" width="200" height="140" />
          <p className="ad-report__chart-empty-title">選擇範圍內無資料</p>
        </div>
      ) : null}

      {/* Table area — only shown when data is available */}
      {!loading && !isEmpty && (
        <div className="ad-report__table-wrap">
          <table className="ad-report__table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} style={{ textAlign: col.align || 'left' }}>{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr key={row.time || idx} className={row._isTotals ? 'ad-report__totals-row' : ''}>
                  {columns.map((col) => (
                    <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
